import { Checkbox } from '@ark-ui/react/checkbox';
import { type Finding } from '@consigli/types';
import clsx from 'clsx';
import { differenceInCalendarDays, format } from 'date-fns';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type MilestoneStatus,
  STATUS_STYLES,
} from '@/pages/insight/dashboard/rfp/rfp-timeline-types';
import {
  computeStatus,
  parseFlexibleDate,
  parseMilestonePayload,
} from '@/pages/insight/dashboard/rfp/rfp-timeline-utils';

import { AddContextCheckboxControl } from './add-context-checkbox';

// A clock time in the stored date string, e.g. "May 20, 2026, 3:30pm".
const TIME_PATTERN = /\d{1,2}:\d{2}/;

const EMPTY_CELL = '–';

/** Shared by the header and every row, so the columns line up down the table. */
export const MILESTONE_TABLE_COLUMNS =
  'grid grid-cols-[var(--ds-space-4)_minmax(0,1fr)_7rem_5rem_8rem] items-center gap-[var(--ds-space-4)] px-[var(--ds-space-4)]';

export interface MilestoneWhen {
  date: string;
  time: string | null;
  /** Null when the stored text is not a date the timeline can read ("TBC", say). */
  status: MilestoneStatus | null;
  daysFromToday: number | null;
}

export const describeMilestoneWhen = (dateStr: string, today: Date): MilestoneWhen => {
  const date = parseFlexibleDate(dateStr);
  if (!date) return { date: dateStr, time: null, status: null, daysFromToday: null };
  return {
    date: format(date, 'd MMM yyyy'),
    time: TIME_PATTERN.test(dateStr) ? format(date, 'h:mm a') : null,
    status: computeStatus(date, today),
    daysFromToday: differenceInCalendarDays(date, today),
  };
};

/** "12 Mar 2024 · 10:30 AM", the time only when the milestone has one. */
export const formatMilestoneWhen = (dateStr: string): string => {
  const { date, time } = describeMilestoneWhen(dateStr, new Date());
  return time ? `${date} · ${time}` : date;
};

/** "tomorrow", "in 3 weeks", "2 months ago", in the reader's language. */
export const formatDaysFromToday = (days: number, locale: string | undefined): string => {
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const distance = Math.abs(days);
  if (distance < 14) return formatter.format(days, 'day');
  if (distance < 60) return formatter.format(Math.round(days / 7), 'week');
  if (distance < 730) return formatter.format(Math.round(days / 30), 'month');
  return formatter.format(Math.round(days / 365), 'year');
};

interface AddContextMilestoneRowProps {
  finding: Finding;
  onSelectedChange: (selected: boolean) => void;
  selected: boolean;
  today: Date;
}

/*
 * One table row, and the checkbox's label like the selectable card, so a click
 * anywhere on the row toggles it.
 */
export const AddContextMilestoneRow: FC<AddContextMilestoneRowProps> = ({
  finding,
  onSelectedChange,
  selected,
  today,
}) => {
  const { t, i18n } = useTranslation();
  const payload = parseMilestonePayload(finding);
  const when = payload ? describeMilestoneWhen(payload.dateStr, today) : null;

  return (
    <Checkbox.Root
      checked={selected}
      onCheckedChange={({ checked }) => onSelectedChange(checked === true)}
      className={clsx(
        MILESTONE_TABLE_COLUMNS,
        'cursor-pointer border-t border-solid border-[var(--ds-color-border-primary)] py-[var(--ds-space-3)] text-left first:border-t-0',
        'text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] tabular-nums',
        selected
          ? 'bg-[var(--ds-color-bg-brand-lighter)]'
          : 'hover:bg-[var(--ds-color-bg-secondary)]',
      )}
    >
      <Checkbox.HiddenInput />
      <AddContextCheckboxControl />
      <span className="wrap-break-word font-[var(--ds-typography-paragraph-paragraph-medium-weight)] text-[var(--ds-color-text-primary)]">
        {payload?.milestone ?? finding.messageTemplate}
      </span>
      <span className="text-[var(--ds-color-text-secondary)]">{when?.date ?? EMPTY_CELL}</span>
      <span className="text-[var(--ds-color-text-secondary)]">{when?.time ?? EMPTY_CELL}</span>
      <span className="flex items-center gap-[var(--ds-space-1-5)] text-[var(--ds-color-text-secondary)]">
        {when?.status && when.daysFromToday !== null ? (
          <>
            <span
              aria-hidden="true"
              className={clsx(
                'size-[var(--ds-space-1-5)] shrink-0 rounded-full',
                STATUS_STYLES[when.status].dot,
              )}
            />
            <span className="sr-only">{t(`dashboard.rfp-timeline.${when.status}`)}</span>
            <span className="first-letter:uppercase">
              {formatDaysFromToday(when.daysFromToday, i18n.resolvedLanguage)}
            </span>
          </>
        ) : (
          EMPTY_CELL
        )}
      </span>
    </Checkbox.Root>
  );
};
